import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getAdministrator,
  createAdministrator,
  updateAdministrator,
} from '@/api/administrators';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, ShieldCheck, Save, Loader2, Eye, EyeOff } from 'lucide-react';

export function AdministratorFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [password, setPassword] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isEdit) {
      fetchAdministrator();
    }
  }, [id, isEdit]);

  const fetchAdministrator = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAdministrator(parseInt(id!));
      setEmail(data.email);
      setFirstname(data.firstname);
      setLastname(data.lastname);
      setEnabled(data.enabled);
      if (data.expiresAt) {
        // Format to datetime-local input format (YYYY-MM-DDTHH:mm)
        const date = new Date(data.expiresAt);
        setExpiresAt(date.toISOString().slice(0, 16));
      }
    } catch (err) {
      setError('Erreur lors du chargement de l\'administrateur');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('L\'email est requis');
      return;
    }
    if (!firstname.trim()) {
      setError('Le prenom est requis');
      return;
    }
    if (!lastname.trim()) {
      setError('Le nom est requis');
      return;
    }
    if (!isEdit && !password) {
      setError('Le mot de passe est requis');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (isEdit) {
        await updateAdministrator(parseInt(id!), {
          email: email.trim(),
          firstname: firstname.trim(),
          lastname: lastname.trim(),
          password: password || undefined,
          expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
          enabled,
        });
        navigate('/administrators');
      } else {
        await createAdministrator({
          email: email.trim(),
          firstname: firstname.trim(),
          lastname: lastname.trim(),
          password,
          expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
          enabled,
        });
        navigate('/administrators');
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          (err instanceof Error ? err.message : 'Erreur lors de la sauvegarde')
      );
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon-sm" asChild>
          <Link to="/administrators">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
            {isEdit ? 'Modifier l\'administrateur V2' : 'Nouvel administrateur V2'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEdit
              ? 'Modifiez les informations de l\'administrateur'
              : 'Creez un nouvel administrateur'}
          </p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Personal info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Informations personnelles</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="firstname">
                  Prenom <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="firstname"
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  placeholder="Ex: Jean"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastname">
                  Nom <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="lastname"
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  placeholder="Ex: Dupont"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Ex: jean.dupont@example.com"
                  required
                />
              </div>
            </CardContent>
          </Card>

          {/* Security */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Securite</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">
                  Mot de passe {!isEdit && <span className="text-red-500">*</span>}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={isEdit ? 'Laisser vide pour ne pas modifier' : 'Mot de passe'}
                    required={!isEdit}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {isEdit && (
                  <p className="text-xs text-muted-foreground">
                    Laissez vide pour conserver le mot de passe actuel
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="expiresAt">Date d'expiration</Label>
                <Input
                  id="expiresAt"
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Laissez vide si le compte n'expire jamais
                </p>
              </div>

              <div className="flex items-center justify-between pt-2">
                <div className="space-y-0.5">
                  <Label htmlFor="enabled">Compte actif</Label>
                  <p className="text-xs text-muted-foreground">
                    Desactivez pour bloquer l'acces
                  </p>
                </div>
                <Switch
                  id="enabled"
                  checked={enabled}
                  onCheckedChange={setEnabled}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/administrators')}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {isEdit ? 'Enregistrer' : 'Creer l\'administrateur'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
